import { useEffect } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { writeSignal } from '@/lib/writeSignal';
import { isUnconfirmedWrite } from '@/lib/apiErrors';
import { qk } from '@/lib/queryKeys';
import { formatPhDate, phDateTimeLocal, todayInPH } from '@/lib/phDate';
import { clearText } from '@/lib/clearToBottom';
import { formatCalMag, formatPair } from '@/lib/pairs';
import { GRID_PARAMS, ownerParam, paramByKey, type ParamDef } from '@/lib/waterModel';
import type { WaterCheckCreate, WaterCheckResponse, WaterCheckUpdate, WaterCheckWithWarnings } from '@/types/generated/openapi';

interface PagedResp<T> {
  data: T[];
  meta?: { cursor?: string | null; has_more?: boolean | null };
}

/** The pre-stocking checks' parameters are the grid's, under the same column names. */
export const CHECK_PARAMS: ParamDef[] = GRID_PARAMS;

/** Every water check of a crop, oldest first. Prep is a bounded stretch, so all pages load. */
export function useWaterChecks(farmId: string, cropId: string) {
  const q = useInfiniteQuery({
    queryKey: qk.waterChecks.crop(farmId, cropId),
    queryFn: ({ pageParam }) =>
      api.get<PagedResp<WaterCheckResponse>>(`/api/v1/farms/${farmId}/water-checks`, {
        params: { cycle_id: cropId, limit: '200', ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    initialPageParam: '',
    getNextPageParam: (last) => (last.meta?.has_more && last.meta.cursor ? last.meta.cursor : undefined),
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = q;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  return {
    checks: q.data?.pages.flatMap((p) => p.data) ?? [],
    loading: q.isLoading || !!hasNextPage,
    isError: q.isError,
  };
}

export function useWaterCheckMutations(farmId: string, cropId: string) {
  const queryClient = useQueryClient();
  const base = `/api/v1/farms/${farmId}/water-checks`;
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: qk.waterChecks.crop(farmId, cropId) });
    queryClient.invalidateQueries({ queryKey: ['water-readings-chart', cropId] });
  };
  const warn = (r: WaterCheckWithWarnings) => {
    if (!r.warnings?.length) return;
    const label = (field: string) => ownerParam(field)?.label ?? field;
    toast(r.warnings.map((w) => w.message.replace(w.field, label(w.field))).join(' · '), { icon: '⚠️', duration: 6000 });
  };

  const create = useMutation({
    // No retry: a create whose response was lost would be sent again and log the check twice.
    retry: false,
    mutationFn: (body: WaterCheckCreate) => api.post<WaterCheckWithWarnings>(base, body, { signal: writeSignal() }),
    onSuccess: (r) => {
      toast.success('Water check saved');
      warn(r);
      refresh();
    },
    // A timeout may have committed: show the journal as it is now so the check isn't entered twice.
    onError: (e) => {
      if (isUnconfirmedWrite(e)) refresh();
    },
  });
  const update = useMutation({
    retry: false,
    mutationFn: ({ id, body }: { id: string; body: WaterCheckUpdate }) =>
      api.put<WaterCheckWithWarnings>(`${base}/${id}`, body, { signal: writeSignal() }),
    onSuccess: (r) => {
      toast.success('Water check updated');
      warn(r);
      refresh();
    },
  });
  const remove = useMutation({
    retry: false,
    mutationFn: (id: string) => api.delete(`${base}/${id}`, { signal: writeSignal() }),
    onSuccess: () => {
      toast.success('Water check deleted');
      refresh();
    },
    onError: (e: Error) => toast.error(e.message || 'Not deleted'),
  });
  return { create, update, remove };
}

export const checkDay = (c: WaterCheckResponse) => formatPhDate(new Date(c.measured_at));

/**
 * Mirrors ensure_can_edit_entry: supervisors+ change any check; a technician only
 * their own, on the PH day the row was created. Delete is supervisor+ only.
 */
export function canEditCheck(c: WaterCheckResponse, canSupervise: boolean, userId: string | undefined) {
  if (canSupervise) return true;
  return !!userId && c.technician_id === userId && formatPhDate(new Date(c.created_at)) === todayInPH();
}

/** The filled parameters of a check, in grid order, as display text. */
export function checkEntries(c: WaterCheckResponse): Array<{ key: string; short: string; text: string }> {
  const row = c as unknown as Record<string, string | number | null | undefined>;
  const alkalinity = formatPair(row.alkalinity_ppm, row.alkalinity_2);
  const calMag = formatCalMag(row.calcium_ppm, row.magnesium_ppm, c.calmag);
  const pairs: Record<string, { short: string; text: string } | null> = {
    alkalinity: alkalinity ? { short: 'Alk', text: `${alkalinity} ppm` } : null,
    calcium: calMag ? { short: 'Cal/Mag', text: calMag } : null,
  };
  return CHECK_PARAMS.flatMap((p) => {
    // A pair is one entry; the second box's key is folded into the first.
    if (p.key in pairs) return pairs[p.key] ? [{ key: p.key, ...pairs[p.key]! }] : [];
    if (p.key === 'alkalinity2' || p.key === 'magnesium') return [];
    const v = row[p.dbColumn];
    if (p.key === 'transparency' && row.transparency_clear_to_bottom) return [{ key: p.key, short: p.short, text: clearText(row.depth_m) }];
    if (v === null || v === undefined || v === '') return [];
    const shown = p.type === 'select' ? (p.options?.find((o) => o.value === v)?.label ?? String(v)) : String(Number(v));
    return [{ key: p.key, short: p.short, text: p.unit ? `${shown} ${p.unit}` : shown }];
  });
}

export function colorLabel(value: string | null | undefined) {
  if (!value) return null;
  return paramByKey('waterColor')?.options?.find((o) => o.value === value)?.label ?? value;
}

/** Sort key for a slot reading's time of day ("3pm" → "15:00"), so it orders against a check's clock time. */
export function slotClock(slot: string): string {
  const m = /^(\d{1,2})(am|pm)$/.exec(slot);
  if (!m) return '00:00';
  const h = (Number(m[1]) % 12) + (m[2] === 'pm' ? 12 : 0);
  return `${String(h).padStart(2, '0')}:00`;
}

/** The chart endpoint's `checks[]` point, as a chart row: date + "HH:mm" in place of a slot. */
export function checkToChartRow<T extends { measured_at: string }>(c: T) {
  const local = phDateTimeLocal(new Date(c.measured_at));
  return { ...c, date: local.slice(0, 10), slot: local.slice(11), kind: 'check' as const };
}
